import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { Page } from './cart.service';
import { formatString } from '../../utils/string-utils';

export enum TransactionStatus {
	PENDING = 'PENDING',
	CANCELLED = 'CANCELLED',
	REJECTED = 'REJECTED',
	APPROVED = 'APPROVED'
}
export enum OrderStatusEnum {
	IN_PROGRESS = 'IN_PROGRESS',
	COMPLETED = 'COMPLETED',
	EXPIRED = 'EXPIRED',
	FAILED = 'FAILED',
	TIMEOUT = 'TIMEOUT'
}
export interface OrderStatus {
	uuid: string;
	userName: string;
	status: OrderStatusEnum;
	requestName: string;
	requestDescription: string;
	created: Date; // date
	completed: Date; // date
	resultExpiration: Date; // date
	errorMessage?: string;
	file?: string;
}

export interface CartTransaction {
	uuid: string;
	userName: string;
	status: TransactionStatus;
	created: Date; // date
	amount: number;
	orderStatus: OrderStatus;
	description: { id: number, pages: Page[], amount: number, title: string }[];
	paypal: string;
	publicDownloadString: string;
	note: string;
}

export interface PrettyPage {
	pageNum: number;
	amount?: number;
	overlayNum?: number;
	sheetNum?: number;
	title: string;
	type: string;
	overview: string;
	thumbnail: string;
	downloadPDF: string;
	download: string;
}

export interface PrettyCartTransaction {
	uuid: string;
	userName: string;
	status: TransactionStatus;
	created?: Date; // date
	amount: number;
	orderStatus: OrderStatus;
	description: { id: number, pages: PrettyPage[], amount: number, title: string, allPages: PrettyPage[] }[];
	paypal: string;
	publicDownloadString: string;
	note: string;
	completion?: string;
	creation?: string;

}

@Injectable({
	providedIn: 'root',
})
export class CartTransactionService {

	private baseUrl = '/aw-server/rest/service/ectransaction'; // Update this URL if needed

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private alertsService: AlertsService,
		private userService: UserService
	) { }

	// Get a single transaction by ID
	getTransaction(transactionId: string): Promise<PrettyCartTransaction> {
		return firstValueFrom(this.http.get<any>(`${this.baseUrl}/${transactionId}`))
			.then(response => this.prettyTransaction(response))  // Apply prettyTransaction
			.catch(this.handleError);
	}

	// Get multiple transactions with pagination and constraints
	getTransactions(page: number, paging: any, constraints: any): Promise<any> {
		const url = `${this.baseUrl}?page=${page}&size=${paging.elementsPerPage}&sort=${paging.sorting.sort}&constraints=${encodeURIComponent(JSON.stringify(constraints))}`;
		return firstValueFrom(this.http.get<any>(url))
			.then(response => {
				response.content = response.content.map((transaction: any) => this.prettyTransaction(transaction)); // Format all transactions
				return response;
			})
			.catch(this.handleError);
	}

	// Download a transaction
	downloadTransaction(uuid: string, paypal: string | null, pdf: boolean): void {
		const url = `${this.baseUrl}/order/download/${uuid}?pdf=${pdf ? true : false}${paypal ? '&payId=' + paypal : ''}`;
		if (paypal) {
			window.location.href = url; // Download for PayPal case
		} else {
			window.open(url, '_blank'); // Download PDF in a new tab
		}
	}

	// Submit a transaction
	submitTransaction(documents: any[], isKiosk: boolean, transaction: string | null): Promise<PrettyCartTransaction> {
		const url = `${this.baseUrl}/process?isKiosk=${isKiosk}&transaction=${transaction}`;
		return firstValueFrom(this.http.put<any>(url, documents, {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' })
		}))
			.then(response => this.prettyTransaction(response)) // Apply prettyTransaction
			.catch(this.handleError);
	}

	// Cancel a transaction
	cancelTransaction(transactionId: string): Promise<CartTransaction> {
		const url = `${this.baseUrl}/cancel?transactionid=${transactionId}`;
		return firstValueFrom(this.http.put<any>(url, null, {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' })
		}))
			.catch(this.handleError);
	}

	// Create a PayPal payment
	createPayPalPayment(documents: any[]): Promise<string> {
		return firstValueFrom(this.http.put(`${this.baseUrl}/paypal/checkout`, documents, {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
			responseType: 'text'
		}))
			.catch(this.handleError);
	}

	// Execute PayPal payment
	executePayPalPayment(pnref: string, securetoken: string, transaction: string): Promise<PrettyCartTransaction> {
		const url = `${this.baseUrl}/paypal/confirm?PNREF=${pnref}&SECURETOKEN=${securetoken}&transaction=${transaction}`;
		return firstValueFrom(this.http.put<any>(url, null, {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' })
		}))
			.then(response => this.prettyTransaction(response)) // Apply prettyTransaction
			.catch(this.handleError);
	}

	// Email download link
	emailDlLink(email: string, transaction: string, path: string): Promise<any> {
		const url = `${this.baseUrl}/download/email?email=${encodeURIComponent(email)}&transaction=${transaction}&path=${encodeURIComponent(path)}`;
		return firstValueFrom(this.http.get<any>(url))
			.catch(this.handleError);
	}

	// Helper function to format transaction data
	prettyTransaction(transaction: PrettyCartTransaction): PrettyCartTransaction {
		if (transaction.orderStatus) {
			const completed = new Date(transaction.orderStatus.completed);
			transaction.completion = completed.toLocaleDateString('en-US');
		}
		const created = transaction.created ? new Date(transaction.created) : new Date();
		transaction.creation = created.toLocaleDateString('en-US') + ' ' + created.toLocaleTimeString('en-US');

		if (!transaction.amount) {
			transaction.amount = 0; // Default to zero dollars
		}

		// Format description and pages
		if (transaction.description && Array.isArray(transaction.description)) {
			transaction.description.forEach((document: any) => {
				const allPages: PrettyPage[] = [];
				document.pages.forEach((page: Page) => {
					if ((page.sheet == null || page.sheet.length == 0) && (page.overlay == null || page.overlay.length == 0)) {
						const title = page.type === "INDEX_PAGE" ? "Index Page" :
							page.type === "STATUS_PAGE" ? "Status Page" :
								"Page " + page.pageNum;

						allPages.push({
							download: (transaction.orderStatus && transaction.orderStatus.status === OrderStatusEnum.COMPLETED)
								? formatString(this.configService.getConfig().transactions.downloadURL, transaction.orderStatus.uuid, document.id, page.pageNum, "ASIS")
								: "",
							downloadPDF: (transaction.orderStatus && transaction.orderStatus.status === OrderStatusEnum.COMPLETED)
								? formatString(this.configService.getConfig().transactions.downloadURL, transaction.orderStatus.uuid, document.id, page.pageNum, "PDF")
								: "",
							thumbnail: formatString(this.configService.getConfig().document.thumbnailPageURL, document.id, page.pageNum, 0, "STANDARD"),
							overview: formatString(this.configService.getConfig().document.overviewPageURL, document.id, page.pageNum, 0, "STANDARD"),
							type: "standard",
							title: title,
							amount: page.ammount,
							pageNum: page.pageNum
						});
					}

					if (page.overlay != null && page.overlay.length > 0) {
						page.overlay.forEach((overlay: any) => {
							allPages.push({
								download: (transaction.orderStatus && transaction.orderStatus.status == OrderStatusEnum.COMPLETED)
									? formatString(this.configService.getConfig().transactions.downloadFullURL, transaction.orderStatus.uuid, document.id, page.pageNum, "OVERLAY", overlay.overlayNum, "ASIS")
									: "",
								downloadPDF: (transaction.orderStatus && transaction.orderStatus.status == OrderStatusEnum.COMPLETED)
									? formatString(this.configService.getConfig().transactions.downloadFullURL, transaction.orderStatus.uuid, document.id, page.pageNum, "OVERLAY", overlay.overlayNum, "PDF")
									: "",
								thumbnail: formatString(this.configService.getConfig().document.thumbnailPageURL, document.id, page.pageNum, 0, "STANDARD"),
								overview: formatString(this.configService.getConfig().document.overviewPageURL, document.id, page.pageNum, 0, "STANDARD"),
								type: "overlay",
								title: "Page " + page.pageNum + " Overlay " + overlay.overlayNum,
								overlayNum: overlay.overlayNum,
								amount: page.ammount,
								pageNum: page.pageNum
							});
						});
					}

					if (page.sheet != null && page.sheet.length > 0) {
						page.sheet.forEach((sheet: any) => {
							allPages.push({
								download: (transaction.orderStatus && transaction.orderStatus.status == OrderStatusEnum.COMPLETED)
									? formatString(this.configService.getConfig().transactions.downloadFullURL, transaction.orderStatus.uuid, document.id, page.pageNum, "SHEET", sheet.sheetNum, "ASIS")
									: "",
								downloadPDF: (transaction.orderStatus && transaction.orderStatus.status == OrderStatusEnum.COMPLETED)
									? formatString(this.configService.getConfig().transactions.downloadFullURL, transaction.orderStatus.uuid, document.id, page.pageNum, "SHEET", sheet.sheetNum, "PDF")
									: "",
								thumbnail: formatString(this.configService.getConfig().document.thumbnailPageURL, document.id, page.pageNum, 0, "STANDARD"),
								overview: formatString(this.configService.getConfig().document.overviewPageURL, document.id, page.pageNum, 0, "STANDARD"),
								type: "sheet",
								title: "Page " + page.pageNum + " Sheet " + sheet.sheetNum,
								sheetNum: sheet.sheetNum,
								amount: page.ammount,
								pageNum: page.pageNum
							});
						});
					}
				});
				document.allPages = allPages;
				if (!document.title) {
					document.title = "Title Unavailable";
				}
			});
		}
		return transaction;
	}

	// Error handling method
	private handleError(error: any): Promise<any> {
		this.alertsService.error({ message: error.message || 'An error occurred!' });
		return Promise.reject(error);
	}
}
